import { useState } from "react";
import { services } from "../data/mock";
import {
  ShieldCheck, ScanEye, Server, Cloud, Headphones, Lightbulb,
  ArrowUpRight, Check, ChevronDown, Sparkles, Target, X
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const iconMap = {
  ShieldCheck, ScanEye, Server, Cloud, Headphones, Lightbulb,
};

const ServiceCard = ({ service, index, onOpen }) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = iconMap[service.icon];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative cursor-pointer group rounded-2xl overflow-hidden"
      style={{
        background: "#121530",
        border: `1px solid ${isHovered ? "rgba(123,104,238,0.3)" : "rgba(123,104,238,0.08)"}`,
        transition: "all 0.4s ease-in-out",
      }}
      onClick={() => onOpen(service)}
    >
      {/* Service Image */}
      <div className="relative overflow-hidden" style={{ height: "200px" }}>
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-700"
          style={{
            transform: isHovered ? "scale(1.08)" : "scale(1)",
            filter: "brightness(0.5) contrast(1.1)",
          }}
        />
        {/* Overlay gradient */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, rgba(15,18,37,0.3) 0%, rgba(18,21,48,0.95) 100%)",
          }}
        />
        {/* Icon floating on image */}
        <div
          className="absolute top-5 left-5 w-11 h-11 flex items-center justify-center rounded-lg"
          style={{
            background: "rgba(123,104,238,0.2)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(123,104,238,0.3)",
          }}
        >
          {IconComponent && <IconComponent size={20} style={{ color: "#A78BFA" }} />}
        </div>
        {/* Number badge */}
        <div
          className="absolute top-5 right-5"
          style={{
            fontFamily: "'Kode Mono', monospace",
            fontSize: "13px",
            color: "rgba(167,139,250,0.5)",
            fontWeight: 600,
          }}
        >
          0{service.id}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "28px 28px 24px" }}>
        <h3
          className="mb-3"
          style={{
            fontFamily: "'Kode Mono', monospace",
            fontSize: "19px",
            fontWeight: 600,
            color: "#F0F0FF",
            lineHeight: 1.3,
          }}
        >
          {service.title}
        </h3>

        <p
          className="mb-5"
          style={{
            fontFamily: "'Kode Mono', monospace",
            fontSize: "13px",
            color: "#8585A8",
            lineHeight: 1.7,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {service.description}
        </p>

        {/* Key Features Preview */}
        <div className="flex flex-wrap gap-2 mb-5">
          {service.features.slice(0, 3).map((feat, i) => (
            <span
              key={i}
              className="rounded-md"
              style={{
                padding: "4px 10px",
                fontSize: "11px",
                fontFamily: "'Kode Mono', monospace",
                background: "rgba(123,104,238,0.08)",
                color: "#9494B8",
                border: "1px solid rgba(123,104,238,0.12)",
              }}
            >
              {feat.length > 30 ? feat.substring(0, 28) + "..." : feat}
            </span>
          ))}
          {service.features.length > 3 && (
            <span
              className="rounded-md"
              style={{
                padding: "4px 10px",
                fontSize: "11px",
                fontFamily: "'Kode Mono', monospace",
                background: "rgba(123,104,238,0.12)",
                color: "#A78BFA",
                border: "1px solid rgba(123,104,238,0.2)",
              }}
            >
              +{service.features.length - 3} more
            </span>
          )}
        </div>

        {/* CTA row */}
        <div
          className="flex items-center justify-between pt-4"
          style={{ borderTop: "1px solid rgba(123,104,238,0.08)" }}
        >
          <span
            className="flex items-center gap-2 transition-all duration-300"
            style={{
              fontFamily: "'Kode Mono', monospace",
              fontSize: "14px",
              fontWeight: 500,
              color: isHovered ? "#A78BFA" : "#7B68EE",
            }}
          >
            View Details
            <ArrowUpRight
              size={15}
              className="transition-transform duration-300"
              style={{
                transform: isHovered ? "translate(2px, -2px)" : "translate(0,0)",
              }}
            />
          </span>
          <ChevronDown
            size={16}
            className="transition-all duration-300"
            style={{
              color: isHovered ? "#A78BFA" : "rgba(123,104,238,0.3)",
            }}
          />
        </div>
      </div>

      {/* Hover top accent */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px] transition-all duration-500"
        style={{
          background: "linear-gradient(90deg, #7B68EE, #60A5FA)",
          opacity: isHovered ? 1 : 0,
        }}
      />
    </motion.div>
  );
};

const ServiceDetailModal = ({ service, onClose }) => {
  const IconComponent = iconMap[service.icon];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
      style={{
        background: "rgba(10,12,28,0.85)",
        backdropFilter: "blur(8px)",
        padding: "40px 20px",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="relative w-full rounded-2xl overflow-hidden"
        style={{
          maxWidth: "960px",
          background: "#121530",
          border: "1px solid rgba(123,104,238,0.2)",
          marginTop: "40px",
          marginBottom: "40px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full border-none cursor-pointer transition-all duration-300"
          style={{
            background: "rgba(15,18,37,0.8)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(123,104,238,0.2)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(123,104,238,0.3)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(15,18,37,0.8)"; }}
        >
          <X size={18} style={{ color: "#C4B5FD" }} />
        </button>

        {/* Hero Image */}
        <div className="relative" style={{ height: "280px" }}>
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.45) contrast(1.15)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(180deg, rgba(18,21,48,0.2) 0%, rgba(18,21,48,1) 100%)",
            }}
          />
          {/* Title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8 pb-6">
            <div className="flex items-center gap-4 mb-4">
              <div
                className="w-12 h-12 flex items-center justify-center rounded-lg"
                style={{
                  background: "rgba(123,104,238,0.2)",
                  border: "1px solid rgba(123,104,238,0.35)",
                }}
              >
                {IconComponent && <IconComponent size={22} style={{ color: "#A78BFA" }} />}
              </div>
              <span
                style={{
                  fontFamily: "'Kode Mono', monospace",
                  fontSize: "13px",
                  color: "#7B68EE",
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                }}
              >
                Service 0{service.id}
              </span>
            </div>
            <h2
              style={{
                fontFamily: "'Kode Mono', monospace",
                fontSize: "clamp(24px, 3vw, 32px)",
                fontWeight: 600,
                color: "#F0F0FF",
                lineHeight: 1.2,
              }}
            >
              {service.title}
            </h2>
          </div>
        </div>

        {/* Content Body */}
        <div style={{ padding: "8px 36px 40px" }}>
          {/* Description */}
          <p
            className="mb-10"
            style={{
              fontFamily: "'Kode Mono', monospace",
              fontSize: "15px",
              color: "#9494B8",
              lineHeight: 1.8,
              maxWidth: "800px",
            }}
          >
            {service.description}
          </p>

          {/* Three Columns: Features, Benefits, Use Cases */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* Key Features */}
            <div
              className="rounded-xl"
              style={{
                padding: "28px 24px",
                background: "rgba(123,104,238,0.05)",
                border: "1px solid rgba(123,104,238,0.1)",
              }}
            >
              <div className="flex items-center gap-3 mb-5">
                <Sparkles size={16} style={{ color: "#7B68EE" }} />
                <h4
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#C4B5FD",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    margin: 0,
                  }}
                >
                  Key Features
                </h4>
              </div>
              <div className="flex flex-col gap-3">
                {service.features.map((feat, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check
                      size={14}
                      className="flex-shrink-0 mt-1"
                      style={{ color: "#7B68EE" }}
                    />
                    <span
                      style={{
                        fontFamily: "'Kode Mono', monospace",
                        fontSize: "13px",
                        color: "#B8B8D0",
                        lineHeight: 1.5,
                      }}
                    >
                      {feat}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div
              className="rounded-xl"
              style={{
                padding: "28px 24px",
                background: "rgba(96,165,250,0.04)",
                border: "1px solid rgba(96,165,250,0.1)",
              }}
            >
              <div className="flex items-center gap-3 mb-5">
                <Target size={16} style={{ color: "#60A5FA" }} />
                <h4
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#93C5FD",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    margin: 0,
                  }}
                >
                  Benefits
                </h4>
              </div>
              <div className="flex flex-col gap-3">
                {service.benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div
                      className="w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-full mt-0.5"
                      style={{ background: "rgba(96,165,250,0.15)" }}
                    >
                      <span style={{ fontSize: "10px", color: "#60A5FA", fontWeight: 700 }}>{i + 1}</span>
                    </div>
                    <span
                      style={{
                        fontFamily: "'Kode Mono', monospace",
                        fontSize: "13px",
                        color: "#B8B8D0",
                        lineHeight: 1.5,
                      }}
                    >
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Use Cases */}
            <div
              className="rounded-xl"
              style={{
                padding: "28px 24px",
                background: "rgba(167,139,250,0.04)",
                border: "1px solid rgba(167,139,250,0.1)",
              }}
            >
              <div className="flex items-center gap-3 mb-5">
                <Lightbulb size={16} style={{ color: "#A78BFA" }} />
                <h4
                  style={{
                    fontFamily: "'Kode Mono', monospace",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#C4B5FD",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    margin: 0,
                  }}
                >
                  Use Cases
                </h4>
              </div>
              <div className="flex flex-col gap-3">
                {service.useCases.map((uc, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <ArrowUpRight
                      size={13}
                      className="flex-shrink-0 mt-1"
                      style={{ color: "#A78BFA" }}
                    />
                    <span
                      style={{
                        fontFamily: "'Kode Mono', monospace",
                        fontSize: "13px",
                        color: "#B8B8D0",
                        lineHeight: 1.5,
                      }}
                    >
                      {uc}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div
            className="flex items-center justify-between flex-wrap gap-4 pt-6 mt-4"
            style={{ borderTop: "1px solid rgba(123,104,238,0.1)" }}
          >
            <p
              style={{
                fontFamily: "'Kode Mono', monospace",
                fontSize: "14px",
                color: "#6B6B8D",
                margin: 0,
              }}
            >
              Interested in this service?
            </p>
            <button
              onClick={() => {
                onClose();
                setTimeout(() => {
                  const el = document.querySelector("#contact");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }, 300);
              }}
              className="inline-flex items-center gap-3 border-none cursor-pointer rounded-lg"
              style={{
                background: "#7B68EE",
                color: "#FFFFFF",
                padding: "12px 24px",
                fontSize: "15px",
                fontWeight: 500,
                fontFamily: "'Kode Mono', monospace",
                transition: "background 0.3s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#6A58DD"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#7B68EE"; }}
            >
              Request a Consultation
              <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <>
      <section
        id="services"
        className="relative w-full"
        style={{ background: "#0B0E1E", padding: "120px 7.6923% 120px" }}
      >
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16 max-w-[1400px] mx-auto"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-[2px] rounded-full" style={{ background: "#7B68EE" }} />
            <span
              className="text-sm tracking-widest uppercase"
              style={{ color: "#7B68EE", fontFamily: "'Kode Mono', monospace" }}
            >
              What We Do
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <h2
                style={{
                  fontFamily: "'Kode Mono', monospace",
                  fontWeight: 600,
                  fontSize: "clamp(32px, 4vw, 48px)",
                  color: "#F0F0FF",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  marginBottom: "16px",
                }}
              >
                Our Services
              </h2>
              <p
                style={{
                  fontFamily: "'Kode Mono', monospace",
                  fontSize: "17px",
                  color: "#8585A8",
                  maxWidth: "600px",
                  lineHeight: 1.6,
                }}
              >
                End-to-end technology solutions engineered for enterprise performance.
                Click any service to explore detailed features, benefits, and use cases.
              </p>
            </div>
            <p
              style={{
                fontFamily: "'Kode Mono', monospace",
                fontSize: "48px",
                fontWeight: 700,
                color: "rgba(123,104,238,0.1)",
                lineHeight: 1,
                margin: 0,
              }}
            >
              0{services.length}
            </p>
          </div>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1400px] mx-auto">
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
              onOpen={setSelectedService}
            />
          ))}
        </div>
      </section>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedService && (
          <ServiceDetailModal
            service={selectedService}
            onClose={() => setSelectedService(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};
